<template>
  <div class="top-vendors-expenses-widget-container">
    <div v-if="loading" class="top-vendors-expenses-widget-loading">Loading...</div>
    <div v-else-if="error" class="top-vendors-expenses-widget-error">{{ error }}</div>
    <table v-else class="top-vendors-expenses-widget-table">
      <thead>
        <tr>
          <th>Vendor</th>
          <th class="top-vendors-expenses-widget-amount-header">Total Expenses</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="vendors.length === 0">
          <td colspan="2" class="top-vendors-expenses-widget-empty">No expense data</td>
        </tr>
        <tr v-else v-for="vendor in vendors" :key="vendor.vendorId">
          <td>{{ vendor.vendorName }}</td>
          <td class="top-vendors-expenses-widget-amount-cell">{{ formatCurrency(vendor.amount) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import './styles/TopVendorsByExpensesWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'TopVendorsByExpensesWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      vendors: [],
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Fetch all vendors for the company first to get vendor names
        const vendors = await ApiService.getAllVendors(this.companyId);
        const vendorsArray = Array.isArray(vendors) ? vendors : [];
        
        // Create vendor map for quick lookup
        const vendorMap = {};
        vendorsArray.forEach(vendor => {
          vendorMap[vendor.id] = vendor.vendorName || vendor.name || `Vendor #${vendor.id}`;
        });

        // Fetch only expenses (not bills)
        const expensesData = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Expenses' });
        const expensesArray = Array.isArray(expensesData) ? expensesData : [];
        
        // Filter to only include expenses (not bills) and exclude voided/cancelled
        const expenses = expensesArray.filter(expense => {
          return expense.type === 'Expense' &&
                 !expense.voided &&
                 expense.status !== 'voided' &&
                 expense.status !== 'cancelled';
        });

        // Aggregate by vendor
        const vendorExpensesMap = new Map();
        
        expenses.forEach(expense => {
          const vendorId = expense.vendorId;
          const vendorName = vendorMap[vendorId] || expense.vendorName || `Vendor #${vendorId}`;
          const totalAmount = parseFloat(expense.originalAmount || expense.totalAmount || 0);
          
          if (vendorExpensesMap.has(vendorId)) {
            const existing = vendorExpensesMap.get(vendorId);
            existing.amount += totalAmount;
          } else {
            vendorExpensesMap.set(vendorId, {
              vendorId: vendorId,
              vendorName: vendorName,
              amount: totalAmount
            });
          }
        });

        // Convert map to array, sort by amount descending, and take top 10
        this.vendors = Array.from(vendorExpensesMap.values())
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 10);
      } catch (error) {
        logger.error('Error fetching top vendors by expenses:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

